import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye, EyeOff, ChefHat, Store, User, Mail, Phone, MapPin, Clock, Lock, ArrowRight, ArrowLeft, Check,
  CreditCard, Star, Infinity, Loader2, Tag, CheckCircle2 } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { getFeatureLabels } from "@/lib/platform-features";
import SlugEditor, { generateSlugFromName, containsProfanity } from "@/components/SlugEditor";
import { fetchCep, formatCep } from "@/lib/cep-utils";
import { isValidName, isValidRestaurantName, isValidBrazilianPhone, isValidEmail } from "@/lib/validation-utils";

interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_monthly: number;
  price_yearly: number | null;
  max_orders_per_month: number | null;
  max_products: number | null;
  max_categories: number | null;
  features: string[];
  is_featured: boolean;
  promo_price_monthly?: number | null;
  promo_price_yearly?: number | null;
  promo_label?: string | null;
  promo_ends_at?: string | null;
}

const STEPS_NEW = [
{ title: "Escolha o Plano", icon: CreditCard },
{ title: "Dados Pessoais", icon: User },
{ title: "Restaurante", icon: Store },
{ title: "Endereço & Horário", icon: Clock },
{ title: "Segurança", icon: Lock }];


const STEPS_EXISTING = [
{ title: "Escolha o Plano", icon: CreditCard },
{ title: "Restaurante", icon: Store },
{ title: "Endereço & Horário", icon: Clock }];


// Slug is now generated in the database trigger (handle_new_user)

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

// Slug is now generated via SlugEditor component

const Cadastro = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [existingUser, setExistingUser] = useState<{id: string;email: string;} | null>(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponApplied, setCouponApplied] = useState<{
    code: string; type: string; value: number; free_days: number | null; billing_cycle: string;
  } | null>(null);

  const [slugValid, setSlugValid] = useState(false);
  const [slugProfanity, setSlugProfanity] = useState(false);
  const [customSlug, setCustomSlug] = useState("");

  const [form, setForm] = useState({
    nome: "", email: "", telefone: "",
    nomeRestaurante: "", tipo: "", cnpj: "", descricao: "", telefoneRestaurante: "",
    cep: "", rua: "", numero: "", bairro: "", cidade: "", estado: "",
    horarioAbertura: "", horarioFechamento: "",
    senha: "", confirmarSenha: ""
  });

  // Check if user is already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const user = session.user;
        // Check if they already have a restaurant
        const { data: rest } = await supabase.from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        if (rest) {
          navigate("/dashboard");
          return;
        }
        setExistingUser({ id: user.id, email: user.email || "" });
      }
      setCheckingAuth(false);
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const fetchPlans = async () => {
      const { data } = await supabase.
      from("plans").
      select("*").
      eq("is_active", true).
      order("sort_order", { ascending: true });
      const parsed = (data as any[] || []).map((p) => ({
        ...p,
        features: getFeatureLabels(p.features)
      }));
      setPlans(parsed);
      const params = new URLSearchParams(window.location.search);
      const planFromUrl = params.get("plan");
      if (planFromUrl && parsed.find((p) => p.id === planFromUrl)) {
        setSelectedPlanId(planFromUrl);
      } else {
        const free = parsed.find((p) => p.price_monthly === 0);
        if (free) setSelectedPlanId(free.id);else
        if (parsed.length > 0) setSelectedPlanId(parsed[0].id);
      }
    };
    fetchPlans();
  }, []);

  const STEPS = existingUser ? STEPS_EXISTING : STEPS_NEW;
  const maxStep = STEPS.length - 1;

  // Map logical step to form content for existing users
  const getContentStep = () => {
    if (!existingUser) return step;
    // STEPS_EXISTING: 0=Plan, 1=Restaurant, 2=Address
    // Map to original content: 0->0, 1->2, 2->3
    const map = [0, 2, 3];
    return map[step] ?? step;
  };

  const [cepLoading, setCepLoading] = useState(false);

  const update = (field: string, value: string) => {
    setForm((p) => ({ ...p, [field]: value }));
    // Auto-generate slug when restaurant name changes
    if (field === "nomeRestaurante" && value) {
      const autoSlug = generateSlugFromName(value);
      setCustomSlug(autoSlug);
    }
  };

  const handleCepChange = useCallback(async (raw: string) => {
    const formatted = formatCep(raw);
    update("cep", formatted);
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 8) {
      setCepLoading(true);
      const data = await fetchCep(digits);
      setCepLoading(false);
      if (data) {
        update("rua", data.logradouro || "");
        update("bairro", data.bairro || "");
        update("cidade", data.localidade || "");
        update("estado", data.uf || "");
        toast({ title: "CEP encontrado!", description: `${data.logradouro}, ${data.localidade} - ${data.uf}` });
      } else {
        toast({ title: "CEP não encontrado", variant: "destructive" });
      }
    }
  }, [toast]);

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponValidating(true);
    try {
      const { data, error } = await supabase
        .from("platform_coupons")
        .select("code, type, value, valid_until, max_uses, uses_count, applicable_plan_ids, billing_cycle, free_days, for_new_users_only")
        .eq("code", couponCode.trim().toUpperCase())
        .eq("is_active", true)
        .maybeSingle();

      if (error || !data) throw new Error("Cupom não encontrado ou inativo");
      if (data.valid_until && new Date(data.valid_until) < new Date()) throw new Error("Cupom expirado");
      if (data.max_uses && data.uses_count >= data.max_uses) throw new Error("Cupom esgotado");
      // Check if coupon is for new users only and user already exists
      if ((data as any).for_new_users_only && existingUser) throw new Error("Este cupom é válido apenas para novos cadastros");
      // Check if coupon applies to selected plan
      const planIds = (data as any).applicable_plan_ids || [];
      if (planIds.length > 0 && !planIds.includes(selectedPlanId)) throw new Error("Este cupom não é válido para o plano selecionado");

      setCouponApplied({
        code: data.code,
        type: data.type,
        value: data.value,
        free_days: (data as any).free_days,
        billing_cycle: (data as any).billing_cycle || "both",
      });
      toast({ title: "🎉 Cupom aplicado com sucesso!" });
    } catch (err: any) {
      toast({ title: "Cupom inválido", description: err.message, variant: "destructive" });
      setCouponApplied(null);
    } finally {
      setCouponValidating(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode("");
  };

  const validateStep = () => {
    const contentStep = getContentStep();
    const checks: Record<number, () => boolean> = {
      0: () => !!selectedPlanId,
      1: () => !!(form.nome && form.email && form.telefone),
      2: () => !!(form.nomeRestaurante && form.tipo && form.telefoneRestaurante && customSlug && (slugValid || slugProfanity)),
      3: () => !!(form.cep && form.rua && form.cidade && form.estado),
      4: () => !!(form.senha && form.confirmarSenha && form.senha === form.confirmarSenha && terms)
    };
    if (!checks[contentStep]()) {
      toast({ title: contentStep === 0 ? "Selecione um plano" : "Preencha todos os campos obrigatórios", variant: "destructive" });
      return false;
    }

    // Enhanced validations per step
    if (contentStep === 1) {
      const nameCheck = isValidName(form.nome);
      if (!nameCheck.valid) {
        toast({ title: "Nome inválido", description: nameCheck.reason, variant: "destructive" });
        return false;
      }
      const emailCheck = isValidEmail(form.email);
      if (!emailCheck.valid) {
        toast({ title: "E-mail inválido", description: emailCheck.reason, variant: "destructive" });
        return false;
      }
      const phoneCheck = isValidBrazilianPhone(form.telefone);
      if (!phoneCheck.valid) {
        toast({ title: "Telefone inválido", description: phoneCheck.reason, variant: "destructive" });
        return false;
      }
    }

    if (contentStep === 2) {
      const restNameCheck = isValidRestaurantName(form.nomeRestaurante);
      if (!restNameCheck.valid) {
        toast({ title: "Nome do restaurante inválido", description: restNameCheck.reason, variant: "destructive" });
        return false;
      }
      const restPhoneCheck = isValidBrazilianPhone(form.telefoneRestaurante);
      if (!restPhoneCheck.valid) {
        toast({ title: "WhatsApp inválido", description: restPhoneCheck.reason, variant: "destructive" });
        return false;
      }
    }

    if (contentStep === 4 && form.senha.length < 8) {
      toast({ title: "A senha deve ter pelo menos 8 caracteres", variant: "destructive" });
      return false;
    }
    return true;
  };

  const next = () => {if (validateStep()) setStep((s) => Math.min(s + 1, maxStep));};
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) return;
    setLoading(true);

    try {
      // Check phone uniqueness
      const phoneToCheck = form.telefoneRestaurante || form.telefone;
      if (phoneToCheck && !existingUser) {
        const { data: isAvailable } = await supabase.rpc("is_phone_available", { 
          check_phone: phoneToCheck 
        });
        
        if (!isAvailable) {
          toast({ 
            title: "Telefone já cadastrado", 
            description: "Este número de telefone já está sendo usado por outro usuário.", 
            variant: "destructive" 
          });
          setLoading(false);
          return;
        }
      }

      const shouldFlag = slugProfanity || containsProfanity(form.nomeRestaurante);

      if (existingUser) {
        // Determine trial days (plan trial + coupon free days)
        let trialDays: number | null = null;
        const selectedPlan = plans.find(p => p.id === selectedPlanId);
        if (couponApplied?.free_days) {
          trialDays = couponApplied.free_days;
        }

        // Already authenticated — insert restaurant directly
        const { data: newRest, error: restError } = await supabase.from("restaurants").insert({
          owner_id: existingUser.id,
          name: form.nomeRestaurante,
          slug: customSlug,
          description: form.descricao || null,
          type: form.tipo || "restaurante",
          cnpj: form.cnpj || null,
          phone: form.telefoneRestaurante || form.telefone || null,
          address_street: form.rua,
          address_number: form.numero || null,
          address_neighborhood: form.bairro || null,
          address_city: form.cidade,
          address_state: form.estado,
          address_zip: form.cep,
          opening_time: form.horarioAbertura || null,
          closing_time: form.horarioFechamento || null,
          plan_id: selectedPlanId || null,
          trial_started_at: trialDays ? new Date().toISOString() : null,
          trial_ends_at: trialDays ? new Date(Date.now() + trialDays * 86400000).toISOString() : null,
        }).select("id").single();
        if (restError) throw restError;

        // Record coupon usage
        if (couponApplied && newRest) {
          // Get coupon id and increment usage
          const { data: couponData } = await supabase.from("platform_coupons")
            .select("id, uses_count")
            .eq("code", couponApplied.code)
            .single();
          if (couponData) {
            await supabase.from("platform_coupons")
              .update({ uses_count: (couponData.uses_count || 0) + 1 } as any)
              .eq("id", couponData.id);
          }
        }

        // Flag if profanity detected
        if (shouldFlag && newRest) {
          await supabase.from("flagged_restaurants").insert({
            restaurant_id: newRest.id,
            restaurant_name: form.nomeRestaurante,
            slug: customSlug,
            owner_id: existingUser.id,
            reason: "profanity_detected",
          });
        }

        // Ensure user has restaurant_admin role
        await supabase.rpc("has_role", { _user_id: existingUser.id, _role: "restaurant_admin" as any }).then(async ({ data: hasRole }) => {
          if (!hasRole) {
            // Role will be added by the trigger for new users, but for existing users we need a different approach
            // Since RLS only allows super_admin to insert roles, we use updateUser metadata trick
          }
        });
        toast({ title: "Restaurante cadastrado com sucesso!" });
        navigate("/dashboard");
      } else {
        // New user signup — trigger handles everything
        const { data: signUpData, error: authError } = await supabase.auth.signUp({
          email: form.email,
          password: form.senha,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              full_name: form.nome,
              phone: form.telefoneRestaurante || form.telefone,
              restaurant_name: form.nomeRestaurante,
              restaurant_type: form.tipo,
              restaurant_description: form.descricao,
              cnpj: form.cnpj,
              address_street: form.rua,
              address_number: form.numero,
              address_neighborhood: form.bairro,
              address_city: form.cidade,
              address_state: form.estado,
              address_zip: form.cep,
              opening_time: form.horarioAbertura,
              closing_time: form.horarioFechamento,
              plan_id: selectedPlanId || "",
              restaurant_slug: customSlug,
              flag_profanity: shouldFlag ? "true" : "",
              coupon_code: couponApplied?.code || "",
              coupon_free_days: couponApplied?.free_days ? String(couponApplied.free_days) : "",
            }
          }
        });

        if (authError) throw authError;

        toast({ title: "Cadastro realizado com sucesso!" });
        navigate("/dashboard");
      }
    } catch (error: any) {
      let msg = error.message || "Erro desconhecido";
      if (msg.includes("already registered") || msg.includes("already exists")) {
        msg = "Este e-mail já está cadastrado. Tente fazer login ou use outro e-mail.";
      } else if (msg.includes("telefone já está cadastrado") || msg.includes("phone")) {
        msg = "Este número de telefone já está cadastrado.";
      } else if (msg.includes("Password should be")) {
        msg = "A senha deve ter pelo menos 6 caracteres.";
      }
      toast({ title: "Erro no cadastro", description: msg, variant: "destructive" });
    } finally {
      setLoading(false);
    }

  };

  const inputClass = "h-12 bg-card border-border";

  const renderStep = () => {
    const contentStep = getContentStep();
    switch (contentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Escolha o plano ideal para seu negócio. Você pode alterar depois.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {plans.map((plan) =>
              <button
                key={plan.id}
                type="button"
                onClick={() => { setSelectedPlanId(plan.id); setCouponApplied(null); setCouponCode(""); }}
                className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                selectedPlanId === plan.id ?
                "border-primary bg-primary/5 ring-1 ring-primary/20" :
                "border-border hover:border-primary/40"}`
                }>
                
                  {plan.is_featured &&
                <Badge className="absolute -top-2 right-3 bg-primary text-primary-foreground text-[10px]">
                      <Star className="h-3 w-3 mr-0.5" /> Popular
                    </Badge>
                }
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-display font-bold">{plan.name}</h3>
                    {selectedPlanId === plan.id &&
                  <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <Check className="h-3 w-3 text-primary-foreground" />
                      </div>
                  }
                  </div>
                  <div className="mb-2">
                    {(() => {
                      const hasPromo = (plan.promo_price_monthly || plan.promo_price_yearly) &&
                        (!plan.promo_ends_at || new Date(plan.promo_ends_at) > new Date());
                      const promoPrice = hasPromo ? (plan.promo_price_monthly || plan.price_monthly) : plan.price_monthly;
                      return hasPromo && promoPrice !== plan.price_monthly ? (
                        <>
                          <span className="text-sm line-through text-muted-foreground mr-1">{formatCurrency(plan.price_monthly)}</span>
                          <span className="text-xl font-bold font-display text-emerald-500">{formatCurrency(promoPrice)}</span>
                          <span className="text-xs text-muted-foreground">/mês</span>
                          {plan.promo_label && (
                            <Badge className="ml-2 bg-emerald-500/20 text-emerald-500 text-[9px]">{plan.promo_label}</Badge>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-xl font-bold font-display">{formatCurrency(plan.price_monthly)}</span>
                          <span className="text-xs text-muted-foreground">/mês</span>
                        </>
                      );
                    })()}
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Pedidos/mês</span>
                      <span className="font-medium">
                        {plan.max_orders_per_month ? plan.max_orders_per_month.toLocaleString("pt-BR") :
                      <span className="flex items-center gap-0.5"><Infinity className="h-3 w-3" />Ilimitado</span>
                      }
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Produtos</span>
                      <span className="font-medium">
                        {plan.max_products ? plan.max_products :
                      <span className="flex items-center gap-0.5"><Infinity className="h-3 w-3" />Ilimitado</span>
                      }
                      </span>
                    </div>
                  </div>
                  {plan.features.length > 0 &&
                <ul className="mt-3 pt-2 border-t border-border space-y-1">
                      {plan.features.slice(0, 3).map((f, i) =>
                  <li key={i} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                          <Check className="h-3 w-3 text-success mt-0.5 shrink-0" />
                          {f}
                        </li>
                  )}
                      {plan.features.length > 3 &&
                  <li className="text-[11px] text-primary font-medium">
                          +{plan.features.length - 3} mais recursos
                        </li>
                  }
                    </ul>
                }
                </button>
              )}
            </div>

            {/* Coupon section */}
            {selectedPlanId && (
              <div className="mt-4 rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">Tem um cupom de desconto?</span>
                </div>
                {couponApplied ? (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      <span className="text-sm font-semibold text-emerald-600">Cupom {couponApplied.code} aplicado!</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {couponApplied.type === "percentage"
                        ? couponApplied.value === 100
                          ? `🎉 Parabéns! Você ganhou ${couponApplied.free_days || 30} dias de acesso gratuito!`
                          : `${couponApplied.value}% de desconto ${couponApplied.billing_cycle === "monthly" ? "no plano mensal" : couponApplied.billing_cycle === "yearly" ? "no plano anual" : "no plano"}`
                        : `R$ ${couponApplied.value.toFixed(2)} de desconto ${couponApplied.billing_cycle === "monthly" ? "no plano mensal" : couponApplied.billing_cycle === "yearly" ? "no plano anual" : "no plano"}`
                      }
                    </p>
                    <Button type="button" variant="ghost" size="sm" onClick={removeCoupon} className="text-xs h-7 text-destructive">
                      Remover cupom
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Digite o código do cupom"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      className="h-10 font-mono text-sm flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={validateCoupon}
                      disabled={couponValidating || !couponCode.trim()}
                      className="h-10 shrink-0"
                    >
                      {couponValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>);

      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome completo *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="nome" placeholder="Seu nome completo" value={form.nome} onChange={(e) => update("nome", e.target.value)} className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-mail *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="email" type="email" placeholder="seu@email.com" value={form.email} onChange={(e) => update("email", e.target.value)} className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefone">Telefone / WhatsApp *</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="telefone" placeholder="(00) 00000-0000" value={form.telefone} onChange={(e) => update("telefone", e.target.value)} className={`${inputClass} pl-10`} />
              </div>
            </div>
          </div>);

      case 2:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nomeRestaurante">Nome do restaurante *</Label>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="nomeRestaurante" placeholder="Nome do seu estabelecimento" value={form.nomeRestaurante} onChange={(e) => update("nomeRestaurante", e.target.value)} className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo de estabelecimento *</Label>
              <Select value={form.tipo} onValueChange={(v) => update("tipo", v)}>
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="restaurante">Restaurante</SelectItem>
                  <SelectItem value="bar">Bar</SelectItem>
                  <SelectItem value="lanchonete">Lanchonete</SelectItem>
                  <SelectItem value="pizzaria">Pizzaria</SelectItem>
                  <SelectItem value="hamburgueria">Hamburgueria</SelectItem>
                  <SelectItem value="dark-kitchen">Dark Kitchen</SelectItem>
                  <SelectItem value="cafeteria">Cafeteria</SelectItem>
                  <SelectItem value="padaria">Padaria</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ (opcional)</Label>
              <Input id="cnpj" placeholder="00.000.000/0000-00" value={form.cnpj} onChange={(e) => update("cnpj", e.target.value)} className={inputClass} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefoneRestaurante">WhatsApp do restaurante *</Label>
              <p className="text-xs text-muted-foreground">Usado para receber pedidos e confirmações via WhatsApp</p>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="telefoneRestaurante" placeholder="(00) 00000-0000" value={form.telefoneRestaurante} onChange={(e) => update("telefoneRestaurante", e.target.value)} className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição do restaurante</Label>
              <textarea
                id="descricao"
                placeholder="Conte um pouco sobre seu estabelecimento..."
                value={form.descricao}
                onChange={(e) => update("descricao", e.target.value)}
                rows={3}
                className="w-full rounded-md bg-card border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none" />
              
            </div>
            <SlugEditor
              value={customSlug}
              onChange={setCustomSlug}
              restaurantName={form.nomeRestaurante}
              onValidityChange={setSlugValid}
              onProfanityDetected={setSlugProfanity}
            />
          </div>);

      case 3:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cep">CEP *</Label>
                <div className="relative">
                  <Input id="cep" placeholder="00000-000" value={form.cep} onChange={(e) => handleCepChange(e.target.value)} className={inputClass} maxLength={9} />
                  {cepLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="numero">Número *</Label>
                <Input id="numero" placeholder="Nº" value={form.numero} onChange={(e) => update("numero", e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="rua">Rua *</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input id="rua" placeholder="Nome da rua" value={form.rua} onChange={(e) => update("rua", e.target.value)} className={`${inputClass} pl-10`} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bairro">Bairro</Label>
                <Input id="bairro" placeholder="Bairro" value={form.bairro} onChange={(e) => update("bairro", e.target.value)} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cidade">Cidade *</Label>
                <Input id="cidade" placeholder="Cidade" value={form.cidade} onChange={(e) => update("cidade", e.target.value)} className={inputClass} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="estado">Estado *</Label>
              <Select value={form.estado} onValueChange={(v) => update("estado", v)}>
                <SelectTrigger className={inputClass}>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"].map((uf) =>
                  <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="horarioAbertura">Abertura</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="horarioAbertura" type="time" value={form.horarioAbertura} onChange={(e) => update("horarioAbertura", e.target.value)} className={`${inputClass} pl-10`} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="horarioFechamento">Fechamento</Label>
                <div className="relative">
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="horarioFechamento" type="time" value={form.horarioFechamento} onChange={(e) => update("horarioFechamento", e.target.value)} className={`${inputClass} pl-10`} />
                </div>
              </div>
            </div>
          </div>);

      case 4:
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="senha">Senha *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="senha"
                  type={showPassword ? "text" : "password"}
                  placeholder="Mínimo 8 caracteres"
                  value={form.senha}
                  onChange={(e) => update("senha", e.target.value)}
                  className={`${inputClass} pl-10 pr-12`} />
                
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmarSenha">Confirmar senha *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmarSenha"
                  type={showPassword ? "text" : "password"}
                  placeholder="Repita a senha"
                  value={form.confirmarSenha}
                  onChange={(e) => update("confirmarSenha", e.target.value)}
                  className={`${inputClass} pl-10`} />
                
              </div>
            </div>
            {form.senha &&
            <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((i) =>
                <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${
                form.senha.length >= i * 3 ? "bg-primary" : "bg-muted"}`
                } />
                )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {form.senha.length < 6 ? "Fraca" : form.senha.length < 10 ? "Média" : "Forte"}
                </p>
              </div>
            }
            <div className="flex items-start gap-2 pt-2">
              <Checkbox id="terms" checked={terms} onCheckedChange={(v) => setTerms(v === true)} className="mt-0.5" />
              <Label htmlFor="terms" className="text-sm text-muted-foreground cursor-pointer leading-snug">
                Li e aceito os <span className="text-primary hover:underline cursor-pointer">Termos de Uso</span> e a{" "}
                <span className="text-primary hover:underline cursor-pointer">Política de Privacidade</span> *
              </Label>
            </div>
          </div>);

    }
  };

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <span className="h-8 w-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-xl">
        
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <ChefHat className="h-8 w-8 text-primary" />
            <span className="text-2xl font-bold text-foreground" style={{ fontFamily: "var(--font-display)" }}>Via Menu </span>
          </Link>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "var(--font-display)" }}>
            {existingUser ? "Cadastre seu restaurante" : "Cadastre seu restaurante"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {existingUser ? `Logado como ${existingUser.email}` : "Comece a receber pedidos em minutos"}
          </p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8 px-2">
          {STEPS.map((s, i) =>
          <div key={i} className="flex items-center gap-1">
              <div className={`flex items-center justify-center w-9 h-9 rounded-full border-2 transition-colors ${
            i < step ? "bg-primary border-primary" :
            i === step ? "border-primary text-primary" :
            "border-border text-muted-foreground"}`
            }>
                {i < step ? <Check className="h-4 w-4 text-primary-foreground" /> : <s.icon className="h-3.5 w-3.5" />}
              </div>
              {i < STEPS.length - 1 &&
            <div className={`hidden sm:block w-8 h-0.5 ${i < step ? "bg-primary" : "bg-border"}`} />
            }
            </div>
          )}
        </div>

        {/* Selected plan badge */}
        {step > 0 && selectedPlan &&
        <div className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="outline" className="text-xs gap-1">
              <CreditCard className="h-3 w-3" />
              Plano: {selectedPlan.name} — {(() => {
                const hasPromo = (selectedPlan.promo_price_monthly) &&
                  (!selectedPlan.promo_ends_at || new Date(selectedPlan.promo_ends_at) > new Date());
                return formatCurrency(hasPromo ? selectedPlan.promo_price_monthly! : selectedPlan.price_monthly);
              })()}/mês
            </Badge>
          </div>
        }

        {/* Card */}
        <div className="glass rounded-2xl p-8">
          <h2 className="text-xl font-semibold text-foreground mb-1" style={{ fontFamily: "var(--font-display)" }}>
            {STEPS[step].title}
          </h2>
          <p className="text-sm text-muted-foreground mb-6">Passo {step + 1} de {STEPS.length}</p>

          <form onSubmit={handleSubmit}>
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}>
              
              {renderStep()}
            </motion.div>

            <div className="flex gap-3 mt-8">
              {step > 0 &&
              <Button type="button" variant="outline" onClick={prev} className="flex-1 h-12">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
                </Button>
              }
              {step < maxStep ?
              <Button type="button" onClick={next} className="flex-1 h-12 bg-gradient-primary text-primary-foreground font-semibold">
                  Próximo <ArrowRight className="h-4 w-4 ml-2" />
                </Button> :

              <Button type="submit" disabled={loading} className="flex-1 h-12 bg-gradient-primary text-primary-foreground font-semibold">
                  {loading ?
                <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {existingUser ? "Cadastrando restaurante..." : "Criando conta..."}
                    </span> :

                existingUser ? "Cadastrar restaurante" : "Criar minha conta"
                }
                </Button>
              }
            </div>
          </form>
        </div>

        {!existingUser && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">ou</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                const { error } = await lovable.auth.signInWithOAuth("google", {
                  redirect_uri: window.location.origin,
                });
                if (error) {
                  toast({ title: "Erro ao entrar com Google", description: String(error), variant: "destructive" });
                }
              }}
              className="w-full h-12 font-semibold text-base gap-3"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Cadastrar com Google
            </Button>

            <p className="text-center text-muted-foreground mt-6">
              Já tem uma conta?{" "}
              <Link to="/login" className="text-primary font-semibold hover:underline">Fazer login</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>);

};

export default Cadastro;